<template>
  <div :class="{ 'lesson-ended-message': !canReconnect }">
    <div class="staticMessage" v-if="showDisclaimerMessage">
      <span class="message">{{ $t('messageDisclaimer') }}</span>
    </div>

    <div v-for="(item, index) in messageList" :key="index">
      <div v-if="isSystemEvent(item)" class="notranslate system-events">
        <div>
          <span>[{{ getFormattedTS(item.ts) }}]&nbsp;</span>
          <span>{{ getEventInfo(item) }}</span>
        </div>
      </div>

      <div v-else :class="['message-list', getMessageTypeClass(item)]">
        <el-avatar>{{ getMessageFrom(item.f) }}</el-avatar>
        <div v-if="canRemoveMessage(item) && item.m && canReconnect" class="delete-view">
          <a
            class="delete-dots"
            @click="
              deleteBalloon = index;
              editBalloon = index;
            "
          >
            <img src="@/assets/img/three_dots@2x.png" />
          </a>
          <span v-show="editBalloon == index" :class="['edit_message_balloon']" @click="editMessage(item)">
            <span>{{ $t('chatBox.editLabel') }}</span>
          </span>
          <span
            v-show="deleteBalloon == index"
            :class="['delete_message_balloon']"
            @click="deleteMessage(item)"
          >
            <span>{{ $t('chatBox.deleteLabel') }}</span>
          </span>
        </div>
        <div class="notranslate info">
          <template v-if="!item.err">
            <span class="time-view">{{ getFormattedTS(item.ts) }}</span>
            <div class="message">
              <span v-if="item.m" style="white-space: pre-line">
                <span v-dompurify-html="item.m" />
                <img v-show="item.u" src="@/assets/img/pencil_icon.svg" class="pencil_icon" />
              </span>
              <span v-else>{{ $t('chatBox.deletedMessage') }}</span>
            </div>
            <div v-if="isMessageSeen(index, item)" class="seen-message">✓ Seen</div>
          </template>
          <template v-else>
            <span class="undelivered-message time-view">
              <i class="el-icon-warning-outline"></i> Not Delivered
            </span>
            <div class="undelivered-message message" v-html="item.m" />
          </template>
          <ChatMessageTagItems v-if="item.wt" :item="item" />
        </div>
      </div>
    </div>

    <div class="endNote" v-if="showEndNote">
      <span class="message">{{ $t(`endNote.${accType}`) }}</span>
    </div>

    <div class="lessonEndNote" v-if="this.isTrainer && this.isLessonEnded">
      <span class="message">{{ $t('lessonEndNote') }}</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { getApiMethod } from '@/modules/apiSelector';

import ChatMessageTagItems from '@/components/Lesson/Chatbox/ChatMessageTagItems';
import userLocalTimezoneMixin from '@/mixins/userLocalTimezone';

export default {
  mixins: [userLocalTimezoneMixin],
  components: { ChatMessageTagItems },
  props: {
    messageList: {
      type: Array,
      required: true
    },
    remoteSeen: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      deleteBalloon: null,
      editBalloon: null,
      loading: false
    };
  },
  watch: {
    messageList(val, oldVal) {
      if (!oldVal.length) {
        this.getTrainerTagSelection();
      }
    }
  },
  mounted() {
    if (this.messageList.length) {
      this.getTrainerTagSelection();
    }
    window.addEventListener('click', this.hideDeleteBalloon);
  },
  computed: {
    ...mapState(['localNickname', 'remoteNickname', 'accType', 'lessonId']),
    ...mapState('connection', ['canReconnect']),
    ...mapState('lesson', ['isLessonWithinGracePeriod']),
    ...mapState(['isTrial']),
    ...mapGetters([
      'isWaitingForLesson',
      'isWaitingOrWarmUpPeriod',
      'nonActualLesson',
      'isLessonOngoing',
      'isLessonEnded',
      'isTrainer'
    ]),
    //shows static message
    showDisclaimerMessage() {
      return this.isWaitingOrWarmUpPeriod;
    },

    //shows end note message
    showEndNote() {
      if (this.isTrial && this.isWaitingForLesson) {
        return false;
      }

      if (this.isTrainer) {
        return this.isLessonWithinGracePeriod;
      } else {
        return this.isLessonEnded;
      }
    }
  },
  methods: {
    async getTrainerTagSelection() {
      if (this.nonActualLesson) {
        return;
      }
      this.loading = false;

      const getTaggedWordHistory = await getApiMethod('getTaggedWordHistory');
      getTaggedWordHistory(this.lessonId);

      this.loading = true;
    },

    // check if item is a system-event
    isSystemEvent(item) {
      return item.hasOwnProperty('e');
    },

    // returns message classname
    getMessageTypeClass(item) {
      const messageType = item.f === this.accType ? 'sent-message' : 'received-message';
      const isDeleted = !item.m ? ' deleted-message' : '';

      return messageType + isDeleted;
    },

    // returns local/ remote name
    getMessageFrom(from) {
      return from == this.accType ? this.localNickname.trim().charAt(0) : this.remoteNickname.trim().charAt(0);
    },

    // returns system event information
    getEventInfo(item) {
      return item.f === this.accType
        ? this.$t(`chatEvents.${item.e}.local`)
        : this.$t(`chatEvents.${item.e}.remote`, [this.remoteNickname]);
    },

    // check if message has been seen and tag it on the last message
    isMessageSeen(index, item) {
      return item.f == this.accType && this.remoteSeen && index === this.messageList.length - 1;
    },

    // get formatted timestamp
    getFormattedTS(ts) {
      const local = this.toLocal(ts);
      const formatted = `${local.time}`;
      return formatted;
    },

    // emit delete message on parent
    deleteMessage(item) {
      this.$emit('deleteMessage', item);
    },

    // check if user can remove message
    canRemoveMessage(item) {
      return !this.disabled && this.accType == item.f && !item.err;
    },

    // hide delete balloon
    hideDeleteBalloon(e) {
      if (!this.deleteBalloon && !this.editBalloon) {
        return;
      }

      if (!this.isDeleteMessageParent(e)) {
        this.deleteBalloon = null;
        this.editBalloon = null;
      }
    },

    // check if clicked element is delete message related
    isDeleteMessageParent(e) {
      const parentClass = e.target.parentNode;
      if (parentClass) {
        return parentClass.classList.contains('delete-view') || parentClass.classList.contains('delete-dots');
      }
    },

    // emit edit message
    editMessage(item) {
      this.$emit('editMessage', item);
    }
  },
  beforeDestroy: function() {
    window.removeEventListener('click', this.hideDeleteBalloon);
  }
};
</script>
